import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Eye, EyeOff, Bot, MessageSquare, Building2,
  Zap, CheckCircle, RefreshCw, Edit3, Plus, Copy,
  ChevronDown, ChevronUp, Loader2, Sparkles, ShieldCheck, AlertTriangle,
  Link2, Calendar, ExternalLink, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

const TABS = [
  { id: "vapi", label: "Vapi AI", icon: Bot },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { id: "clinic", label: "Clinic Info", icon: Building2 },
  { id: "integrations", label: "Integrations", icon: Link2 },
];

const DEFAULT_PROMPT = `You are a professional AI receptionist for {clinic_name}. Help patients book appointments, answer questions about clinic hours and services, and provide general information. Always be warm, empathetic and professional.

For booking appointments, collect:
- Patient full name
- Phone number
- Preferred appointment date and time
- Reason for visit (brief)

After collecting all details, use the book_appointment function to confirm the booking. Let the patient know their appointment is confirmed and provide any relevant clinic information.

For availability questions, use the check_availability function to provide open slots.

If a patient has an emergency, always direct them to call 911 or go to the nearest emergency room.`;

export default function Settings() {
  const [tab, setTab] = useState("vapi");
  const [settings, setSettings] = useState({ clinic_name: "Careline", vapi_assistant_id: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  // Assistant state
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [creating, setCreating] = useState(false);

  // Connection test
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // null | "ok" | "error"
  const [testMsg, setTestMsg] = useState("");

  // Prompt editor state
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [editedFirstMessage, setEditedFirstMessage] = useState("");
  const [promptDirty, setPromptDirty] = useState(false);

  // New assistant form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newPrompt, setNewPrompt] = useState(DEFAULT_PROMPT);
  const [newFirstMessage, setNewFirstMessage] = useState("Hello! Thank you for calling {clinic_name}. I'm your AI receptionist. How can I assist you today?");

  // Integrations state
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(null); // null=loading, true/false
  const [testingCalendly, setTestingCalendly] = useState(false);
  const [calendlyTestResult, setCalendlyTestResult] = useState(null); // null | "ok" | "error"
  const [calendlyTestMsg, setCalendlyTestMsg] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  // Check if Google OAuth is configured on the platform
  useEffect(() => {
    axios.get(`${API}/integrations/google/status`, { headers: authHeader() })
      .then(r => {
        setGoogleConfigured(r.data.configured);
        if (r.data.connected) setSettings(p => ({ ...p, _google_oauth_connected: true }));
      })
      .catch(() => setGoogleConfigured(false));
  }, []);

  // Listen for popup postMessage when Google OAuth completes
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "google_connected") {
        setSettings(p => ({
          ...p,
          _google_oauth_connected: true,
          google_calendar_email: e.data.email || p.google_calendar_email,
        }));
        toast.success(`Google Calendar connected${e.data.email ? ` (${e.data.email})` : ""}!`);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { headers: authHeader() });
      setSettings(prev => ({ ...prev, ...data }));
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  };

  const fetchAssistants = useCallback(async () => {
    setLoadingAssistants(true);
    try {
      const { data } = await axios.get(`${API}/vapi/assistants`, { headers: authHeader() });
      setAssistants(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to fetch assistants"); }
    finally { setLoadingAssistants(false); }
  }, []);

  useEffect(() => {
    if (tab === "vapi") fetchAssistants();
  }, [tab, fetchAssistants]);

  const testConnection = async () => {
    const keyToTest = settings.vapi_private_api_key;
    const isMasked = !keyToTest || keyToTest.startsWith("****");
    // If key is masked, send null — backend will test the currently saved key
    // If key is blank and not masked, user hasn't entered one yet
    if (!keyToTest && !isMasked) {
      toast.error("Enter your Vapi Private API Key first");
      return;
    }
    setTesting(true);
    setTestResult(null);
    setTestMsg("");
    try {
      const { data } = await axios.post(
        `${API}/vapi/test-connection`,
        { api_key: isMasked ? null : keyToTest },
        { headers: authHeader() }
      );
      setTestResult("ok");
      setTestMsg(data.message || "Connection successful!");
      toast.success(data.message || "Vapi connection is working!");
      fetchAssistants();
    } catch (err) {
      setTestResult("error");
      const msg = err.response?.data?.detail || "Connection failed — check your Private API Key";
      setTestMsg(msg);
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  // Load assistant prompt when one is selected or assistant ID changes
  const loadAssistantPrompt = useCallback(async (id) => {
    if (!id) return;
    setLoadingPrompt(true);
    setPromptExpanded(true);
    try {
      const { data } = await axios.get(`${API}/vapi/assistants/${id}`, { headers: authHeader() });
      setSelectedAssistant(data);
      const sysMsg = data?.model?.messages?.find(m => m.role === "system");
      setEditedPrompt(sysMsg?.content || "");
      setEditedFirstMessage(data?.firstMessage || "");
      setPromptDirty(false);
    } catch { toast.error("Failed to load assistant details"); }
    finally { setLoadingPrompt(false); }
  }, []);

  // When the stored assistant ID changes, auto-load it
  useEffect(() => {
    if (settings.vapi_assistant_id && tab === "vapi") {
      loadAssistantPrompt(settings.vapi_assistant_id);
    }
  }, [settings.vapi_assistant_id, tab, loadAssistantPrompt]);

  const savePrompt = async () => {
    const id = selectedAssistant?.id || settings.vapi_assistant_id;
    if (!id) return toast.error("No assistant selected");
    setSavingPrompt(true);
    try {
      await axios.patch(
        `${API}/vapi/assistants/${id}`,
        { system_prompt: editedPrompt, first_message: editedFirstMessage },
        { headers: authHeader() }
      );
      toast.success("Prompt saved to Vapi!");
      setPromptDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save prompt");
    } finally { setSavingPrompt(false); }
  };

  const createAssistant = async () => {
    setCreating(true);
    const clinicName = settings.clinic_name || "Careline";
    const resolvedPrompt = newPrompt.replace(/{clinic_name}/g, clinicName);
    const resolvedFirst = newFirstMessage.replace(/{clinic_name}/g, clinicName);
    try {
      const { data } = await axios.post(
        `${API}/vapi/create-assistant`,
        { system_prompt: resolvedPrompt, first_message: resolvedFirst },
        { headers: authHeader() }
      );
      if (data.success) {
        toast.success("New AI Assistant created!");
        setShowNewForm(false);
        await fetchAssistants();
        await fetchSettings();
      } else {
        toast.error(`Failed: ${data.error}`);
      }
    } catch { toast.error("Failed to create assistant"); }
    finally { setCreating(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const update = { ...settings };
      if (update.twilio_auth_token?.startsWith("****")) delete update.twilio_auth_token;
      if (update.meta_access_token?.startsWith("****")) delete update.meta_access_token;
      if (update.vapi_private_api_key?.startsWith("****")) delete update.vapi_private_api_key;
      await axios.put(`${API}/settings`, update, { headers: authHeader() });
      toast.success("Settings saved!");
      fetchSettings();
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const toggleSecret = (key) => setShowSecrets(p => ({ ...p, [key]: !p[key] }));

  // Google Calendar connect
  const connectGoogleCalendar = async () => {
    if (googleConfigured === false) {
      toast.error("Google OAuth credentials are not configured on this server. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the backend environment.");
      return;
    }
    setConnectingGoogle(true);
    try {
      const { data } = await axios.get(`${API}/integrations/google/auth-url`, { headers: authHeader() });
      const url = data.auth_url ?? data.url;
      if (url) {
        const popup = window.open(url, "google_oauth", "width=500,height=650");
        if (!popup) toast.error("Popup blocked — please allow popups for this site.");
      } else {
        toast.error("Failed to get Google auth URL");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect Google Calendar");
    } finally {
      setConnectingGoogle(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await axios.delete(`${API}/integrations/google/disconnect`, { headers: authHeader() });
      setSettings(p => ({ ...p, _google_oauth_connected: false, google_calendar_email: "" }));
      toast.success("Google Calendar disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  // Calendly test
  const testCalendly = async () => {
    if (!settings.calendly_api_key) {
      toast.error("Enter your Calendly API Key first");
      return;
    }
    setTestingCalendly(true);
    setCalendlyTestResult(null);
    setCalendlyTestMsg("");
    try {
      const { data } = await axios.post(
        `${API}/integrations/calendly/test`,
        {},
        { headers: authHeader() }
      );
      setCalendlyTestResult("ok");
      setCalendlyTestMsg(data.message || "Calendly connected successfully!");
      toast.success(data.message || "Calendly is working!");
    } catch (err) {
      setCalendlyTestResult("error");
      const msg = err.response?.data?.detail || "Calendly connection failed — check your API Key";
      setCalendlyTestMsg(msg);
      toast.error(msg);
    } finally {
      setTestingCalendly(false);
    }
  };

  const Field = ({ label, field, type = "text", placeholder = "", hint = "" }) => (
    <div>
      <label className="text-xs font-medium text-slate-400 block mb-1">{label}</label>
      <div className="relative">
        <input
          type={type === "secret" ? (showSecrets[field] ? "text" : "password") : type}
          value={settings?.[field] || ""}
          onChange={e => setSettings(p => ({ ...p, [field]: e.target.value }))}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
        />
        {type === "secret" && (
          <button type="button" onClick={() => toggleSecret(field)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
            {showSecrets[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );

  if (loading) return <div className="p-8 text-slate-400">Loading settings...</div>;

  const googleConnected = !!(settings._google_oauth_connected || settings.google_access_token || settings.google_calendar_email);
  const calendlyConnected = !!(settings.calendly_api_key && calendlyTestResult === "ok");

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold font-outfit text-white">Settings</h1>
        <p className="text-slate-400 text-sm">Configure your integrations and clinic details</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0A0D14] p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id
                ? "bg-[#161B27] text-cyan-400 shadow-sm"
                : "text-slate-500 hover:text-slate-300"
            }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-[#161B27] border border-white/10 rounded-xl p-6">
        {/* ── VAPI TAB ── */}
        {tab === "vapi" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold font-outfit text-white">Vapi AI Configuration</h2>
                <p className="text-xs text-slate-500 mt-0.5">Connect your Vapi account for voice AI calls</p>
              </div>
              {testResult === "ok" ? (
                <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1.5 rounded-full">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  Vapi Connected
                </div>
              ) : testResult === "error" ? (
                <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2.5 py-1.5 rounded-full">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                  Connection Failed
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-white/5 border border-white/10 px-2.5 py-1.5 rounded-full">
                  <div className="w-1.5 h-1.5 bg-slate-600 rounded-full" />
                  Not tested
                </div>
              )}
            </div>

            {/* Private API Key — the critical one for REST sync */}
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-400">Private API Key required for call sync</p>
                  <p className="text-xs text-amber-500/80 mt-0.5">
                    The Public Key (used in the browser widget) won't work for REST API calls.
                    Go to <strong>dashboard.vapi.ai → Account → API Keys</strong> and copy the <strong>Private Key</strong>.
                  </p>
                </div>
              </div>
              <Field label="Vapi API Key" field="vapi_private_api_key" type="secret"
                placeholder="Paste your Private Key from dashboard.vapi.ai"
                hint="Used for: syncing calls, listing assistants, editing prompts" />
              <button onClick={testConnection} disabled={testing}
                className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors">
                {testing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</>
                  : <><ShieldCheck className="w-4 h-4" /> Test Connection</>
                }
              </button>
              {testResult && (
                <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
                  testResult === "ok"
                    ? "bg-green-500/10 text-green-400 border border-green-500/20"
                    : "bg-red-500/10 text-red-400 border border-red-500/20"
                }`}>
                  {testResult === "ok"
                    ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  }
                  {testMsg}
                </div>
              )}
            </div>

            <Field label="Vapi Phone Number" field="vapi_phone_number"
              placeholder="+1 (501) 451 0490"
              hint="The phone number configured in your Vapi dashboard" />
            <Field label="Vapi Assistant ID" field="vapi_assistant_id"
              placeholder="Leave empty to auto-create"
              hint="Your Vapi assistant ID — select from list below or create a new one" />

            {/* ── Webhook Info ── */}
            <div className="border border-blue-500/20 rounded-xl p-4 bg-blue-500/5">
              <h3 className="font-medium text-blue-300 text-sm mb-2">Webhook URL</h3>
              <p className="text-xs text-blue-400/70 mb-2">Set this in Vapi dashboard → Assistant → Server URL:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-[#0A0D14] border border-white/10 rounded px-2 py-1.5 text-blue-300 flex-1 block">
                  {process.env.REACT_APP_BACKEND_URL}/api/vapi/webhook
                </code>
                <button onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/vapi/webhook`)}
                  className="text-blue-400 hover:text-blue-200 p-1.5 rounded hover:bg-blue-500/10">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* ── Assistant List ── */}
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-white/5 border-b border-white/10">
                <h3 className="font-medium text-white text-sm flex items-center gap-2">
                  <Bot className="w-4 h-4 text-blue-400" />
                  Your Assistants
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={fetchAssistants} disabled={loadingAssistants}
                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 px-2.5 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingAssistants ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                  <button onClick={() => { setShowNewForm(v => !v); setPromptExpanded(false); }}
                    className="flex items-center gap-1.5 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    <Plus className="w-3.5 h-3.5" />
                    New Assistant
                  </button>
                </div>
              </div>

              {/* Assistant rows */}
              <div className="divide-y divide-white/5">
                {loadingAssistants && (
                  <div className="flex items-center justify-center py-6 text-slate-500 gap-2 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading assistants...
                  </div>
                )}
                {!loadingAssistants && assistants.length === 0 && (
                  <div className="text-center py-6 text-slate-500 text-sm">
                    No assistants found. Create one below.
                  </div>
                )}
                {!loadingAssistants && assistants.map(a => {
                  const isActive = settings.vapi_assistant_id === a.id;
                  const isSelected = selectedAssistant?.id === a.id;
                  return (
                    <div key={a.id}
                      className={`px-4 py-3 flex items-center gap-3 transition-colors ${isSelected ? "bg-blue-500/10" : "hover:bg-white/5"}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? "bg-green-500" : "bg-slate-600"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{a.name}</p>
                        <p className="text-xs text-slate-500 font-mono truncate">{a.id}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isActive && (
                          <span className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full font-medium">Active</span>
                        )}
                        <button onClick={() => { setSettings(p => ({ ...p, vapi_assistant_id: a.id })); loadAssistantPrompt(a.id); }}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                            isActive
                              ? "border-blue-500/30 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20"
                              : "border-white/10 text-slate-400 hover:bg-white/5"
                          }`}>
                          {isActive ? "Edit Prompt" : "Use & Edit"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Prompt Editor (for selected assistant) ── */}
            {(selectedAssistant || settings.vapi_assistant_id) && !showNewForm && (
              <div className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setPromptExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white/5 border-b border-white/10 hover:bg-white/5 transition-colors">
                  <h3 className="font-medium text-white text-sm flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-violet-400" />
                    Edit Assistant Prompt
                    {promptDirty && <span className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">Unsaved changes</span>}
                  </h3>
                  {promptExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </button>

                {promptExpanded && (
                  <div className="p-4 space-y-4">
                    {loadingPrompt ? (
                      <div className="flex items-center gap-2 text-slate-500 text-sm py-4 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading prompt...
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs font-semibold text-slate-400 block mb-1.5">
                            First Message <span className="text-slate-500 font-normal">(what the AI says when the call connects)</span>
                          </label>
                          <input
                            value={editedFirstMessage}
                            onChange={e => { setEditedFirstMessage(e.target.value); setPromptDirty(true); }}
                            placeholder="Hello! Thank you for calling..."
                            className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold text-slate-400">
                              System Prompt <span className="text-slate-500 font-normal">(instructions for the AI)</span>
                            </label>
                            <span className="text-xs text-slate-500">{editedPrompt.length} chars</span>
                          </div>
                          <textarea
                            value={editedPrompt}
                            onChange={e => { setEditedPrompt(e.target.value); setPromptDirty(true); }}
                            rows={12}
                            placeholder="You are a professional AI receptionist for..."
                            className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono resize-y"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Tip: Mention clinic hours, services, and any special instructions for handling patient calls.
                          </p>
                        </div>

                        <div className="flex items-center gap-3 pt-1">
                          <button onClick={savePrompt} disabled={savingPrompt || !promptDirty}
                            className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors">
                            {savingPrompt
                              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving to Vapi...</>
                              : <><Save className="w-4 h-4" /> Save Prompt to Vapi</>
                            }
                          </button>
                          {!promptDirty && !savingPrompt && (
                            <span className="text-xs text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-3.5 h-3.5" /> Saved
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── New Assistant Form ── */}
            {showNewForm && (
              <div className="border border-blue-500/20 rounded-xl overflow-hidden bg-blue-500/5">
                <div className="flex items-center justify-between px-4 py-3 bg-blue-500/10 border-b border-blue-500/20">
                  <h3 className="font-medium text-blue-300 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-400" />
                    Create New Assistant
                  </h3>
                  <button onClick={() => setShowNewForm(false)}
                    className="text-xs text-slate-500 hover:text-slate-300">Cancel</button>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-400 block mb-1.5">
                      First Message
                    </label>
                    <input
                      value={newFirstMessage}
                      onChange={e => setNewFirstMessage(e.target.value)}
                      placeholder="Hello! Thank you for calling..."
                      className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">Use <code className="bg-white/10 px-1 rounded">{"{clinic_name}"}</code> as a placeholder — it's auto-replaced.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-slate-400">System Prompt</label>
                      <button onClick={() => setNewPrompt(DEFAULT_PROMPT)}
                        className="text-xs text-blue-400 hover:underline">Reset to default</button>
                    </div>
                    <textarea
                      value={newPrompt}
                      onChange={e => setNewPrompt(e.target.value)}
                      rows={14}
                      className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono resize-y"
                    />
                  </div>

                  <button onClick={createAssistant} disabled={creating}
                    className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {creating
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                      : <><Zap className="w-4 h-4" /> Create Assistant</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── WHATSAPP TAB ── */}
        {tab === "whatsapp" && (
          <div className="space-y-6">
            <div>
              <h2 className="font-semibold font-outfit text-white">WhatsApp Configuration</h2>
              <p className="text-xs text-slate-500 mt-0.5">Configure Twilio and/or Meta WhatsApp</p>
            </div>

            {/* Twilio */}
            <div className="border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-red-500 flex items-center justify-center">
                  <MessageSquare className="w-3 h-3 text-white" />
                </div>
                <h3 className="font-medium text-white">Twilio WhatsApp</h3>
              </div>
              <div className="space-y-3">
                <Field label="Account SID" field="twilio_account_sid" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" hint="From your Twilio Console dashboard" />
                <Field label="Auth Token" field="twilio_auth_token" type="secret" placeholder="Your Twilio auth token" />
                <Field label="WhatsApp Number" field="twilio_whatsapp_number" placeholder="+14155238886" hint="Your Twilio WhatsApp-enabled number" />
              </div>
              <div className="mt-4 p-3 bg-[#0A0D14] rounded-lg border border-white/10">
                <p className="text-xs text-slate-500 font-medium mb-1">Twilio Webhook URL:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-slate-400 flex-1">{process.env.REACT_APP_BACKEND_URL}/api/whatsapp/twilio/webhook</code>
                  <button onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/twilio/webhook`)}
                    className="text-slate-500 hover:text-slate-300"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>

            {/* Meta */}
            <div className="border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center">
                  <MessageSquare className="w-3 h-3 text-white" />
                </div>
                <h3 className="font-medium text-white">Meta WhatsApp Cloud API</h3>
              </div>
              <div className="space-y-3">
                <Field label="Access Token" field="meta_access_token" type="secret" placeholder="Your Meta permanent access token" />
                <Field label="Phone Number ID" field="meta_phone_number_id" placeholder="Phone number ID from Meta Business" />
                <Field label="WhatsApp Business Account ID" field="meta_waba_id" placeholder="WABA ID" />
                <Field label="Webhook Verify Token" field="meta_verify_token" placeholder="clinic_verify_2025" hint="Set this as your verify token in Meta Business Manager" />
              </div>
              <div className="mt-4 p-3 bg-[#0A0D14] rounded-lg border border-white/10 space-y-2">
                <p className="text-xs text-slate-500 font-medium">Meta Webhook URLs:</p>
                {[
                  `GET  ${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/meta/webhook`,
                  `POST ${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/meta/webhook`,
                ].map(url => (
                  <div key={url} className="flex items-center gap-2">
                    <code className="text-xs text-slate-400 flex-1">{url}</code>
                    <button onClick={() => copyToClipboard(url.replace(/^(GET|POST)\s+/, ""))}
                      className="text-slate-500 hover:text-slate-300"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CLINIC TAB ── */}
        {tab === "clinic" && (
          <div className="space-y-5">
            <div>
              <h2 className="font-semibold font-outfit text-white">Clinic Information</h2>
              <p className="text-xs text-slate-500 mt-0.5">Used by the AI receptionist and landing page</p>
            </div>
            <Field label="Clinic Name" field="clinic_name" placeholder="Your Clinic Name" />
            <Field label="Clinic Phone" field="clinic_phone" placeholder="+1 (555) 000-0000" />
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1">Clinic Address</label>
              <textarea
                value={settings?.clinic_address || ""}
                onChange={e => setSettings(p => ({ ...p, clinic_address: e.target.value }))}
                rows={3}
                placeholder="123 Medical Drive, Suite 100..."
                className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        )}

        {/* ── INTEGRATIONS TAB ── */}
        {tab === "integrations" && (
          <div className="space-y-6">
            <div>
              <h2 className="font-semibold font-outfit text-white">Calendar Integrations</h2>
              <p className="text-xs text-slate-500 mt-0.5">Connect Google Calendar and Calendly to auto-sync appointments</p>
            </div>

            {/* Google Calendar Section */}
            <div className="border border-white/10 bg-[#161B27] rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white text-sm">Google Calendar</h3>
                    <p className="text-xs text-slate-500">Sync appointments to Google Calendar automatically</p>
                  </div>
                </div>
                <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                  googleConnected
                    ? "bg-green-500/15 text-green-400 border border-green-500/25"
                    : "bg-white/5 text-slate-500 border border-white/10"
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${googleConnected ? "bg-green-500 animate-pulse" : "bg-slate-600"}`} />
                  {googleConnected ? "Connected" : "Not Connected"}
                </span>
              </div>

              {googleConnected && settings.google_calendar_email && (
                <div className="flex items-center gap-2 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                  <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  Connected as <span className="text-white font-medium">{settings.google_calendar_email}</span>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Calendar ID</label>
                <input
                  type="text"
                  value={settings?.google_calendar_id || ""}
                  onChange={e => setSettings(p => ({ ...p, google_calendar_id: e.target.value }))}
                  placeholder="primary or your-email@gmail.com"
                  className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Use "primary" for your main calendar or a specific calendar ID</p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {googleConfigured === false ? (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    Google OAuth not configured — add <code className="font-mono mx-1">GOOGLE_CLIENT_ID</code> &amp; <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to server env
                  </div>
                ) : googleConnected ? (
                  <button
                    onClick={disconnectGoogleCalendar}
                    className="flex items-center gap-2 text-red-400 border border-red-500/20 bg-red-500/5 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-500/10 transition-colors"
                  >
                    <X className="w-4 h-4" /> Disconnect
                  </button>
                ) : (
                  <button
                    onClick={connectGoogleCalendar}
                    disabled={connectingGoogle || googleConfigured === null}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {connectingGoogle
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting...</>
                      : <><ExternalLink className="w-4 h-4" /> Connect Google Calendar</>
                    }
                  </button>
                )}
              </div>
            </div>

            {/* Calendly Section */}
            <div className="border border-white/10 bg-[#161B27] rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Link2 className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white text-sm">Calendly</h3>
                    <p className="text-xs text-slate-500">Connect Calendly for online appointment scheduling</p>
                  </div>
                </div>
                <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                  calendlyConnected
                    ? "bg-green-500/15 text-green-400 border border-green-500/25"
                    : "bg-white/5 text-slate-500 border border-white/10"
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${calendlyConnected ? "bg-green-500 animate-pulse" : "bg-slate-600"}`} />
                  {calendlyConnected ? "Connected" : "Not Connected"}
                </span>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Calendly API Key</label>
                <div className="relative">
                  <input
                    type={showSecrets["calendly_api_key"] ? "text" : "password"}
                    value={settings?.calendly_api_key || ""}
                    onChange={e => setSettings(p => ({ ...p, calendly_api_key: e.target.value }))}
                    placeholder="Your Calendly Personal Access Token"
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  />
                  <button type="button" onClick={() => toggleSecret("calendly_api_key")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showSecrets["calendly_api_key"] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Get your token from Calendly → Integrations → API & Webhooks</p>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Calendly Event Type URL</label>
                <input
                  type="text"
                  value={settings?.calendly_event_type_url || ""}
                  onChange={e => setSettings(p => ({ ...p, calendly_event_type_url: e.target.value }))}
                  placeholder="https://calendly.com/your-name/30min"
                  className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">The Calendly event type URL to use for booking</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={testCalendly}
                  disabled={testingCalendly}
                  className="flex items-center gap-2 bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-600/30 disabled:opacity-50 transition-colors"
                >
                  {testingCalendly
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</>
                    : <><ShieldCheck className="w-4 h-4" /> Test Calendly</>
                  }
                </button>
              </div>

              {calendlyTestResult && (
                <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
                  calendlyTestResult === "ok"
                    ? "bg-green-500/10 text-green-400 border border-green-500/20"
                    : "bg-red-500/10 text-red-400 border border-red-500/20"
                }`}>
                  {calendlyTestResult === "ok"
                    ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  }
                  {calendlyTestMsg}
                </div>
              )}
            </div>

            {/* Webhook Info Box */}
            <div className="border border-cyan-500/20 rounded-xl p-4 bg-cyan-500/5">
              <div className="flex items-start gap-3">
                <Zap className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-cyan-300 text-sm mb-1">Automatic Calendar Sync</h3>
                  <p className="text-xs text-cyan-400/70">
                    When patients call Vapi, appointments auto-sync to your connected calendar. No manual action needed — every booking made through the AI receptionist is instantly added to Google Calendar or Calendly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save button — shown on all tabs except integrations has its own save logic via handleSave */}
        <div className="mt-6 pt-5 border-t border-white/10">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
